import { Tooltip } from '@analog/ui/src';
import Badge from 'components/common/Badge';
import { Skeleton } from 'components/Skeleton';
import TableColumns from 'components/Table/TableColumns';
import { FontColors } from 'consts';
import moment from 'moment';
import { NavigateFunction } from 'react-router-dom';
import { routes } from 'routes/routesConst';
import { ColumnData } from 'types';
import
  {
    colors,
    colorType,
    DBtoStringValueMapping,
    getIcon,
    getNetworkName,
    hexToString,
    parseNetwork,
    timeAgo,
  } from 'utils';
import { createDetailNavigation } from 'utils/navigation';

import { tooltipOfPhase } from './GMPMessageColumns';

interface TaskColumnProps {
  navigate: NavigateFunction;
}

export const SubmitTaskColumns = ({ navigate }: TaskColumnProps) => {
  const handleSubmitTaskClick = createDetailNavigation(navigate, routes.submitGateway);
  const columns = [
    {
      title: 'Batch ID',
      key: 'batchId',
      render: (data: ColumnData) => (
        <div className="flex col-span-2 md:col-span-1">
          <Tooltip
            id={`${data[data.columnKey]}`}
            content={`${data[data.columnKey]}`}
            className="!w-full sm:!w-fit !break-words !whitespace-normal z-10 !rounded-lg"
            clickable
          />
          <TableColumns
            dataTooltipId={`${data[data.columnKey]}`}
            value={`${data[data.columnKey]}` || '-'}
            classNames={{
              content: data[data.columnKey] ? 'cursor-pointer w-fit' : 'w-fit',
              skeletonText: 'h-4 w-10',
              skeletonLabel: 'w-10',
            }}
            textColor={data[data.columnKey] ? FontColors.PURPLE : FontColors.WHITE}
            onClick={() => {
              data[data.columnKey].toString() &&
                handleSubmitTaskClick(data[data.columnKey].toString());
            }}
          />
        </div>
      ),
    },
    {
      title: 'Task ID',
      key: 'taskId',
      render: (data: ColumnData) => (
        <>
          <Tooltip
            id={data[data.columnKey]?.toString() || ''}
            content={data[data.columnKey]?.toString() || ''}
            className="!w-full sm:!w-fit !break-words !whitespace-normal z-10 !rounded-lg"
          />
          <TableColumns
            dataTooltipId={`${data[data.columnKey]}`}
            value={data[data.columnKey] || '-'}
            classNames={{
              content: data[data.columnKey] ? 'cursor-pointer w-fit' : 'w-fit',
              skeletonText: 'h-4 w-10',
              skeletonLabel: 'w-10',
            }}
            textColor={FontColors.PURPLE}
            onClick={() => {
              data[data.columnKey]?.toString() &&
                navigate(`${routes.submitGateway}/${data.taskId.toString()}`, {
                  state: { isTaskId: true },
                });
            }}
          />
        </>
      ),
    },
    {
      title: 'Network',
      key: 'network',
      render: (data: ColumnData) => {
        if (!data.network) {
          return (
            <Skeleton.Loader>
              <span>-</span>
            </Skeleton.Loader>
          );
        }
        const networkName = getNetworkName(data.network);
        return (
          <Skeleton.Loader>
            <div className="flex flex-col gap-2">
              <TableColumns
                key={data[data.columnKey]}
                beforeImagePath={networkName && getIcon(networkName)}
                value={parseNetwork(data.network)}
                textColor={FontColors.WHITE}
                classNames={{
                  container: '!w-fit',
                  base: '!w-fit',
                  content: 'capitalize',
                  skeletonText: 'h-4 w-12',
                }}
              />
            </div>
          </Skeleton.Loader>
        );
      },
    },
    {
      title: 'Status',
      key: 'phase',
      render: (data: ColumnData) => {
        const val = data[data.columnKey]?.toUpperCase();
        return (
          <>
            <Tooltip
              id={tooltipOfPhase[val as keyof typeof tooltipOfPhase]}
              content={tooltipOfPhase[val as keyof typeof tooltipOfPhase]}
              className="max-w-[220px] md:!max-w-fit !rounded-lg z-10"
              clickable
            />
            <Skeleton.Loader className="w-20 h-5">
              <Badge
                label={
                  DBtoStringValueMapping[val?.toLowerCase() as keyof typeof DBtoStringValueMapping]
                }
                badgeColor={colors[val?.toLowerCase() as keyof typeof colors] as colorType}
                data-tooltip-id={tooltipOfPhase[val as keyof typeof tooltipOfPhase]}
              />

              {data?.result?.err && (
                <>
                  <Tooltip
                    id="failed"
                    content={hexToString(data?.result?.err)}
                    className="max-w-[220px] !rounded-lg z-10"
                    clickable
                  />
                  <Badge label="FAILED" badgeColor="red" data-tooltip-id="failed" />
                </>
              )}
            </Skeleton.Loader>
          </>
        );
      },
    },
    {
      title: 'Created at',
      key: 'created_at',
      render: (data: ColumnData) => (
        <TableColumns
          value={data[data.columnKey] ? timeAgo(new Date(data[data.columnKey])) : '-'}
          dataTooltipId={data[data.columnKey]}
          dataTooltipContent={moment.utc(data[data.columnKey]).format('YYYY-MM-DD hh:mm:ss (UTC)')}
          toolTipClass="!w-fit"
        />
      ),
    },
    {
      title: 'Updated at',
      key: 'updated_at',
      render: (data: ColumnData) => (
        <TableColumns
          value={data[data.columnKey] ? timeAgo(new Date(data[data.columnKey])) : '-'}
          dataTooltipId={data[data.columnKey]}
          dataTooltipContent={moment.utc(data[data.columnKey]).format('YYYY-MM-DD hh:mm:ss (UTC)')}
          toolTipClass="!w-fit"
        />
      ),
    },
  ];

  return columns;
};
